import {DateTime} from 'luxon';
import React, {FC, useCallback} from 'react';
import styled from 'styled-components';

import {TimeFormatsEnum} from '../../../../libs/shared/core/src/helpers/datetime/dateTimeHelpers';
import {SmartDateTimeFormatter} from '../../../../libs/shared/ui/src/components/datetime/smartDateTimeFormatter';
import {Dropdown} from '../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {PopoverCoordinator} from '../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {useMeasureElement} from '../../../../libs/shared/ui/src/hooks/domHookHelpers';
import {ellipsis} from '../../../../libs/shared/ui/src/styles/mixins';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {DateTimePicker} from '../dateTime/dateTimePicker';
import {InlineEditingInputDisplay} from './inlineEditingInputDisplay';

/*
 * Types.
 */

interface InlineEditingDateTimePickerProps {
  value: string;
  onEdit: (arg: string) => void;
  onEditSave: (arg: string) => void;
  selectedValue: string;
  timeFormat: TimeFormatsEnum;
  shouldStartInEditMode?: boolean;
}

/*
 * Style.
 */

const StyledInlineEditingInputDisplay = styled(InlineEditingInputDisplay)`
  margin: 3px 0 2px 0;
`;

const StyledPopoverCoordinator = styled(PopoverCoordinator)`
  width: 100%;
`;

const StyledValueButtonDiv = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: ${(p) => p.theme.greys.shade20};
  color: ${(p) => p.theme.greys.shade90};
  border-radius: 4px;
  padding: 5px 8px;
`;

const StyledDateTimeDisplayDiv = styled.div`
  display: flex;
  align-items: center;
  white-space: nowrap;
  overflow: hidden;
`;

const StyledDateTimeFormatter = styled.span`
  ${ellipsis()}
`;

const StyledCalendarIcon = styled(Icon)`
  margin-right: 8px;
`;

/*
 * Component.
 */

export const InlineEditingDateTimePicker: FC<InlineEditingDateTimePickerProps> = (props) => {
  const theme = useUserTheme();
  const {value, onEdit, onEditSave, selectedValue, timeFormat, shouldStartInEditMode} = props;

  const [popoverButtonRef, {width: popoverButtonWidth}] = useMeasureElement();

  const onDateTimeSelect = useCallback(
    (dt: DateTime | undefined) => {
      if (!dt) {
        return;
      }

      onEdit(dt.toMillis().toString());
    },
    [onEdit],
  );

  const selectedDateTime = selectedValue ? DateTime.fromMillis(Number(selectedValue)) : DateTime.local();

  return (
    <StyledInlineEditingInputDisplay>
      <StyledPopoverCoordinator
        renderButton={() => (
          <StyledValueButtonDiv ref={popoverButtonRef}>
            <StyledDateTimeDisplayDiv>
              <StyledCalendarIcon name="calendarSmall" color={theme.greys.shade70} />
              <StyledDateTimeFormatter>
                <SmartDateTimeFormatter value={selectedDateTime.valueOf()} />
              </StyledDateTimeFormatter>
            </StyledDateTimeDisplayDiv>
            <Icon name="chevronDownSmall" color={theme.greys.shade70} />
          </StyledValueButtonDiv>
        )}
        renderModal={(popoverProps) => (
          <Dropdown {...popoverProps} minWidth={popoverButtonWidth}>
            <DateTimePicker
              selectedDateTime={selectedDateTime}
              timeFormat={timeFormat}
              onDateTimeSelect={onDateTimeSelect}
            />
          </Dropdown>
        )}
        onPopoverToggle={(isPopoverOpen) => {
          // User clicks out of the datepicker dropdown to save
          if (!isPopoverOpen) {
            onEditSave(value);
          }
        }}
        isModalInitiallyOpen={shouldStartInEditMode}
      />
    </StyledInlineEditingInputDisplay>
  );
};
